import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  Users,
  RefreshCw,
  Loader2,
  Search,
  ArrowLeft,
  Mail,
  Phone,
  Calendar,
  UserMinus,
  UserPlus,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

import { API_BASE_URL } from '@/lib/apiBase';

const API = API_BASE_URL;

const LocationUsersPage = () => {
  const { locationId } = useParams();
  const { isAdmin, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState('');
  const [location, setLocation] = useState(null);
  
  // Request removal state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [removalNotes, setRemovalNotes] = useState('');
  const [isSelfRemoval, setIsSelfRemoval] = useState(false);

  // Check if a CRM user is the current logged-in user (by email match)
  const isCurrentUsersCrmAccount = (crmUser) => {
    return currentUser?.email?.toLowerCase() === crmUser?.email?.toLowerCase();
  };
  
  // All users can request removal for any CRM user in their assigned location
  // (The location access check is already handled by the API)
  const canRequestRemoval = () => true;

  const fetchLocation = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/locations/${locationId}`);
      setLocation(response.data);
    } catch (error) {
      console.error('Failed to fetch location:', error);
    }
  }, [locationId]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/locations/${locationId}/crm-users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  useEffect(() => {
    fetchLocation();
    fetchUsers();
  }, [fetchLocation, fetchUsers]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/locations/${locationId}/sync-users`);
      toast.success(response.data.message);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to sync users');
    } finally {
      setSyncing(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    setDeleting(true);
    
    try {
      await axios.post(`${API}/sync-requests/user-removal`, {
        location_id: locationId,
        crm_user_id: userToDelete.id,
        crm_user_name: userToDelete.name,
        notes: removalNotes || null,
      });
      toast.success('Removal request submitted');
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      setRemovalNotes('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit removal request');
    } finally {
      setDeleting(false);
    }
  };

  const filteredUsers = users.filter(user =>
    user.name?.toLowerCase().includes(search.toLowerCase()) ||
    user.email?.toLowerCase().includes(search.toLowerCase()) ||
    user.role?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="location-users-page">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <Link to="/locations" className="flex items-center gap-2 text-slate-600 hover:text-slate-900 w-fit">
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Locations</span>
        </Link>
        
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              CRM Users
            </h1>
            <p className="text-slate-500 mt-1">
              {location?.name} • {users.length} users synced
            </p>
          </div>
          {isAdmin() && (
            <Button 
              onClick={handleSync}
              disabled={syncing}
              data-testid="sync-users-btn"
            >
              {syncing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Sync from CRM
            </Button>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search users..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="search-users"
        />
      </div>

      {/* Users List */}
      {filteredUsers.length === 0 ? (
        <Card className="border-dashed border-2 border-slate-200">
          <CardContent className="py-12 text-center">
            <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {search ? 'No users match your search' : 'No users synced'}
            </h3>
            <p className="text-slate-500 mb-6">
              {search ? 'Try a different search term' : 'Click "Sync from CRM" to pull users from the CRM location'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredUsers.map((user) => (
            <Card key={user.id} className={`overflow-hidden ${isCurrentUsersCrmAccount(user) ? 'ring-2 ring-indigo-500 ring-offset-2' : ''}`} data-testid={`user-card-${user.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 ${isCurrentUsersCrmAccount(user) ? 'bg-indigo-500' : 'bg-indigo-100'} rounded-full flex items-center justify-center`}>
                      <span className={`${isCurrentUsersCrmAccount(user) ? 'text-white' : 'text-indigo-600'} font-semibold text-sm`}>
                        {user.name?.charAt(0)?.toUpperCase() || 'U'}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-slate-900 truncate">{user.name}</h3>
                        {isCurrentUsersCrmAccount(user) && (
                          <Badge className="bg-indigo-100 text-indigo-700 text-xs">You</Badge>
                        )}
                      </div>
                      {user.role && (
                        <Badge variant="outline" className="mt-1 text-xs">
                          {user.role}
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  {/* Any user can request removal for users in their assigned location */}
                  {canRequestRemoval() && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`${isCurrentUsersCrmAccount(user) ? 'text-red-600 hover:text-red-700 hover:bg-red-50' : 'text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50'} -mr-2`}
                      onClick={() => {
                        setUserToDelete(user);
                        setIsSelfRemoval(isCurrentUsersCrmAccount(user));
                        setDeleteDialogOpen(true);
                      }}
                      data-testid={`remove-user-${user.id}`}
                      title={isCurrentUsersCrmAccount(user) ? 'Request my removal from this location' : 'Request user removal'}
                    >
                      <UserMinus className="w-4 h-4" />
                    </Button>
                  )}
                </div>

                <div className="mt-4 space-y-2">
                  {user.email && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Mail className="w-4 h-4 text-slate-400" />
                      <span className="truncate">{user.email}</span>
                    </div>
                  )}
                  {user.phone && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Phone className="w-4 h-4 text-slate-400" />
                      <span>{user.phone}</span>
                    </div>
                  )}
                  {user.calendar_ids?.length > 0 && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Calendar className="w-4 h-4 text-slate-400" />
                      <span>{user.calendar_ids.length} calendar(s)</span>
                    </div>
                  )}
                </div>

                <div className="mt-3 pt-3 border-t text-xs text-slate-400">
                  Synced: {new Date(user.synced_at).toLocaleDateString()}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Removal Request Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={(open) => {
        setDeleteDialogOpen(open);
        if (!open) {
          setRemovalNotes('');
          setIsSelfRemoval(false);
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isSelfRemoval ? 'Request My Removal' : `Request Removal from ${location?.name || 'Location'}`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isSelfRemoval 
                ? `This will create a request to remove your account from ${location?.name || 'this location'}. An administrator will review and process your request.`
                : `This will create a request for support to remove "${userToDelete?.name}" from ${location?.name || 'this location'}. This will NOT automatically delete the user in CRM.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          {isSelfRemoval && (
            <div className="text-amber-600 text-sm font-medium bg-amber-50 p-3 rounded-md">
              Note: Your calendar access will be removed once approved.
            </div>
          )}

          <div className="py-2">
            <label className="text-sm font-medium text-slate-700">
              {isSelfRemoval ? 'Reason for leaving (optional)' : 'Notes (optional)'}
            </label>
            <textarea
              value={removalNotes}
              onChange={(e) => setRemovalNotes(e.target.value)}
              className="mt-2 w-full min-h-[90px] rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder={isSelfRemoval ? 'Please share why you are requesting removal (optional)' : 'Add any context for support (optional)'}
            />
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteUser}
              disabled={deleting}
              className={isSelfRemoval ? "bg-red-600 hover:bg-red-700" : "bg-indigo-600 hover:bg-indigo-700"}
              data-testid="confirm-remove-user"
            >
              {deleting ? 'Submitting...' : 'Submit Request'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default LocationUsersPage;
